import { useGetInfiniteProductsList } from '@shared/api/hooks/pcf/products';
import { ProductList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import { useDebounce } from '@shared/components/data-table/hooks/use-debounce';
import Loader from '@shared/components/loader';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command';
import { Input } from '@shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface ProductComparisonCommandProps {
  product_a: ProductList['uuid'];
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  type?: 'select' | 'command';
}

export function ProductComparisonCommand({
  product_a,
  setOpen,
  type = 'command',
}: ProductComparisonCommandProps) {
  const navigate = useNavigate();

  const { ref, inView } = useInView();
  const [search, setSearch] = useState('' as string);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInfiniteProductsList({
    status: ['complete'],
    page_size: 10,
    search: debouncedSearch || undefined,
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, inView, hasNextPage]);

  if (isError) <div className="p-4 text-sm">Error fetching products</div>;

  if (type === 'select') {
    return (
      <div className="space-y-1">
        <Text>Compare with</Text>
        <Select
          onValueChange={(value) => {
            navigate({
              pathname: '/product-comparison',
              search: new URLSearchParams({
                product_a: product_a,
                product_b: value.toString(),
              }).toString(),
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select another product" />
          </SelectTrigger>
          <SelectContent
            align="start"
            className="max-h-[300px]"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div
              className="flex items-center border-b px-3 pb-2"
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Search className="mr-2 size-4 shrink-0 opacity-50" />
              <Input
                ref={inputRef}
                placeholder="Search products..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  // Maintain focus after state update
                  // setTimeout(() => inputRef.current?.focus(), 0);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className="h-8 border-0 p-0 ring-0 focus:border-0 focus:outline-none focus:ring-0"
              />
            </div>
            <SelectGroup className="max-h-[240px] overflow-y-auto overflow-x-hidden">
              {!data && isLoading ? (
                <div className="flex justify-center py-4">
                  <Loader size="sm" />
                </div>
              ) : data?.pages
                  .flatMap((p) => p.results)
                  .filter((product) => product?.uuid !== product_a).length ===
                0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  No products found.
                </div>
              ) : (
                <>
                  {data?.pages.map((page) => {
                    return page.results
                      ?.filter((product) => product.uuid !== product_a)
                      .map((product) => {
                        return (
                          <SelectItem
                            value={product.uuid}
                            key={product.uuid}
                            className="w-auto"
                            endContent={
                              product.emissionsCategory ? (
                                <RatingCloud
                                  status={product.status}
                                  rating={
                                    product.emissionsCategory as RatingCloudProps['rating']
                                  }
                                />
                              ) : undefined
                            }
                          >
                            <div className="max-w-md">
                              {capitalizeFirstLetter(product.name)}
                            </div>
                          </SelectItem>
                        );
                      });
                  })}
                  <div ref={ref}>
                    {isFetchingNextPage && (
                      <Loader size="sm" className="my-2" />
                    )}
                  </div>
                </>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <Command shouldFilter={false} className="max-w-lg">
      <CommandInput
        placeholder="Search for products..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          {!data && isLoading ? (
            <Loader size="sm" className="" />
          ) : (
            'No products found.'
          )}
        </CommandEmpty>
        <CommandGroup>
          {data?.pages.map((page) => {
            return page.results
              ?.filter((product) => product.uuid !== product_a)
              .map((product) => {
                return (
                  <CommandItem
                    key={product.uuid}
                    value={product.uuid}
                    onSelect={async (value) => {
                      navigate({
                        pathname: '/product-comparison',
                        search: new URLSearchParams({
                          product_a: product_a,
                          product_b: value,
                        }).toString(),
                      });
                      setOpen(false);
                    }}
                    className="flex min-h-10 w-full justify-between"
                  >
                    <span>{product.name} </span>
                    {product.emissionsCategory && (
                      <RatingCloud
                        status={product.status}
                        rating={
                          product.emissionsCategory as RatingCloudProps['rating']
                        }
                      />
                    )}
                  </CommandItem>
                );
              });
          })}
          <div ref={ref}>
            {isFetchingNextPage && <Loader size="sm" className="my-2" />}
          </div>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
